import { useSearchParams } from "next/navigation";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import CheckBox from "@/components/CheckBox";
import { FilterOutlineIcon } from "@/components/Icons";
import PageChange from "@/components/PageChange";
import useFetch from "@/hooks/useFetch";
import useProductsFilter, { addQueryPrefix } from "@/hooks/useProductsFilter";
import { getCategoryAttributes } from "@/requests/category";
import { getFilterAds } from "@/requests/ads";
import { debounce } from "@/utils";
import { currentPageQuery } from "@/utils/constants";
import {
  Accordion,
  CloseIcon,
  ComboboxData,
  RangeSlider,
  Select,
} from "@mantine/core";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "@/hooks/useCompatRouter";
import { getS3ImageUrl, getPageRoute } from "@/utils/routes";
import { FilterItemsAccordion, FilterItemsCategoryTree } from "../FilterItems";
import ProductListWidget from "../ProductListWidget";
import classes from "./Body.module.css";

const filterQueries = {
  sortBy: addQueryPrefix("sortBy"),
  sort: addQueryPrefix("sort"),
  price: addQueryPrefix("price"),
  id: addQueryPrefix("id"),
  onlineExclusive: addQueryPrefix("isOnline"),
  inStock: addQueryPrefix("inStock"),
  outOfStock: addQueryPrefix("outOfStock"),
  preOrder: addQueryPrefix("preOrder"),
  comingSoon: addQueryPrefix("comingSoon"),
};

const sortByData: ComboboxData = [
  {
    value: `${filterQueries.sortBy}=id&${filterQueries.sort}=DESC`,
    label: "New Arrivals",
  },
  {
    value: `${filterQueries.sortBy}=price&${filterQueries.sort}=ASC`,
    label: "Price Low to High",
  },
  {
    value: `${filterQueries.sortBy}=price&${filterQueries.sort}=DESC`,
    label: "Price High to Low",
  },
];

const MIN_PRICE = 0;
const MAX_PRICE = 1_000_000;

// Map the value from the 1500-15000 range to the 0-100 range
function valueLabelParse(value: number, max?: number, min?: number) {
  const minRange = min || MIN_PRICE;
  const maxRange = max || MAX_PRICE;
  const mappedValue = ((value - minRange) * 100) / (maxRange - minRange);
  return mappedValue;
}

// Map the value from the 0-100 range to the 1500-15000 range
function valueLabelFormat(value: number, max?: number, min?: number) {
  const minRange = min || MIN_PRICE;
  const maxRange = max || MAX_PRICE;
  const mappedValue = (value / 100) * (maxRange - minRange) + minRange;

  // console.log({ value, mappedValue, minRange, maxRange });

  return Math.round(mappedValue);
}

interface Props {
  className?: string;
  style?: CSSProperties;
  type?: CategoryPageType;

  products?: ProductItemV2[];
  total?: number;
  category?: Partial<CategoryItem>;
  loading?: boolean;
  productsFilter: ReturnType<typeof useProductsFilter>;

  widgets?: Widget[];
  hideFilter?: boolean;
  /** brand pages reuse this component — their slug has no category attributes */
  skipAttributes?: boolean;
  subcategories?: CategoryItem[];
  highestPrice?: number;
  lowestPrice?: number;
  perPage?: number;

  onFilter?: (key: string, value: string) => void;
  onUrlFilter?: (url: URLSearchParams) => void;
  loadNextPage?: (page: number) => void;
  setActivePage?: (page: number) => void;
  setPerPage?: (perPage: number) => void;

  brandSlug?: string;
}

const pid = "product-list-body-12123";

const Body = ({
  className,
  style,
  type = "default",

  products,
  total = 0,
  category,
  loading = false,
  productsFilter,
  hideFilter = false,
  skipAttributes = false,

  brandSlug = "",

  widgets = [],
  subcategories = [],
  lowestPrice: _lowestPrice = 0,
  highestPrice: _highestPrice = 0,
  // highestPrice = 0,
  // lowestPrice = 0,
  perPage = 20,

  onFilter,
  onUrlFilter = () => {},
  loadNextPage = () => {},
  setActivePage = () => {},
  setPerPage = () => {},
}: Props) => {
  const { query } = useRouter();
  const currentYear = new Date().getFullYear();

  const filterAttributes = useFetch(
    () => getCategoryAttributes(category?.slug || ""),
    {
      deps: [category?.slug],
      // brand pages pass the BRAND slug as category — /category/filter/:slug
      // would always 404 there
      skip: skipAttributes || !category?.slug,
    }
  );

  const highestPrice = useMemo(() => _highestPrice, [_highestPrice]);

  const lowestPrice = useMemo(() => _lowestPrice, [_lowestPrice]);

  const searchParams = useSearchParams();

  const price = useMemo(
    () => searchParams?.get(filterQueries.price),
    [searchParams]
  );

  const onlineExclusiveQuery = useMemo(
    () => searchParams?.get(filterQueries.onlineExclusive),
    [searchParams]
  );

  const inStockQuery = useMemo(
    () => searchParams?.get(filterQueries.inStock),
    [searchParams]
  );

  const outOfStockQuery = useMemo(
    () => searchParams?.get(filterQueries.outOfStock),
    [searchParams]
  );

  const preOrderQuery = useMemo(
    () => searchParams?.get(filterQueries.preOrder),
    [searchParams]
  );

  const comingSoonQuery = useMemo(
    () => searchParams?.get(filterQueries.comingSoon),
    [searchParams]
  );

  // Extract brand slug from filter query params (e.g. ?gg__Brand=some-brand)
  const filterBrandSlug = useMemo(() => {
    if (brandSlug) return brandSlug;
    let slug = "";
    searchParams.forEach((value, key) => {
      const label = key.replace("gg__", "");
      if (label.toLowerCase().includes("brand") && value && !slug) {
        slug = value;
      }
    });
    return slug;
  }, [brandSlug, searchParams]);

  const categorySlug = useMemo(
    () => (brandSlug ? "" : category?.slug || ""),
    [brandSlug, category?.slug]
  );

  const [min, max, high, low] = useMemo(() => {
    if (price) {
      return price.split(",").map((p) => +p);
    }
    return [undefined, undefined, MAX_PRICE];
  }, [price]);

  const value1 =
    min !== undefined
      ? valueLabelParse(min, highestPrice || high, lowestPrice || low)
      : 0;

  const value2 =
    max !== undefined
      ? valueLabelParse(max, highestPrice || high, lowestPrice || low)
      : 100;

  const [value, setValue] = useState<[number, number]>([value1, value2]);
  const [minPrice, setMinPrice] = useState<number>(lowestPrice || MIN_PRICE);
  const [maxPrice, setMaxPrice] = useState<number>(highestPrice || MAX_PRICE);
  const [staticFilter, setStaticFilter] = useState({
    onlineExclusive: false,
    inStock: false,
    outOfStock: false,
  });

  const filterAttrs: Filter[] = [
    {
      id: 123456789,
      name: "Availability",
      label: "Availability",
      type: "singleSelect",
      options:
        '[{"valueId":11,"value":"Online Exclusive"},{"valueId":22,"value":"In stock"},{"valueId":33,"value":"Out of Stock"}]',
      status: "active",
      createdAt: "2024-10-23T17:38:06.730Z",
      updatedAt: "2024-10-23T17:38:06.730Z",
      deletedAt: null,
    },
  ];

  const filterAds = useFetch(
    () =>
      getFilterAds({
        category_slug: categorySlug,
        brand_slug: filterBrandSlug,
      }),
    {
      deps: [categorySlug, filterBrandSlug],
    }
  );

  const pageLoading = useRef(true);

  // useEffect(() => {
  // 	const timeout = setTimeout(() => {
  // 		const minPrice = valueLabelFormat(
  // 			value[0],
  // 			highestPrice,
  // 			lowestPrice
  // 		);
  // 		const maxPrice = valueLabelFormat(
  // 			value[1],
  // 			highestPrice,
  // 			lowestPrice
  // 		);

  // 		const priceQueryParam = addQueryPrefix("price");

  // 		const url = new URLSearchParams();
  // 		if (value[0] === 0 && value[1] === 100) {
  // 			url.set(priceQueryParam, "");
  // 		} else if (
  // 			priceQueryParam in query &&
  // 			minPrice === MIN_PRICE &&
  // 			maxPrice === MAX_PRICE
  // 		) {
  // 			url.set(priceQueryParam, `${minPrice},${maxPrice}`);
  // 		} else {
  // 			let price = "";

  // 			price += minPrice || 0;
  // 			if (maxPrice) price += `,${maxPrice}`;

  // 			url.set(priceQueryParam, price);
  // 		}

  // 		onUrlFilter(url);
  // 	}, 500);

  // 	return () => {
  // 		clearTimeout(timeout);
  // 	};
  // }, [value]);

  const updatePriceToQuery = useCallback(
    (value: [number, number]) => {
      const minPrice = valueLabelFormat(value[0], highestPrice, lowestPrice);
      const maxPrice = valueLabelFormat(value[1], highestPrice, lowestPrice);

      // console.log({ minPrice, maxPrice });
      // console.log({ highestPrice, lowestPrice });
      // console.log({ value });

      const priceQueryParam = addQueryPrefix("price");

      const url = new URLSearchParams();
      if (value[0] === 0 && value[1] === 100) {
        url.set(priceQueryParam, "");
      } else if (
        priceQueryParam in query &&
        minPrice === MIN_PRICE &&
        maxPrice === MAX_PRICE
      ) {
        url.set(priceQueryParam, `${minPrice},${maxPrice}`);
      } else {
        let price = "";

        price += minPrice || 0;
        if (maxPrice) price += `,${maxPrice}`;

        url.set(priceQueryParam, price);
      }

      onUrlFilter(url);
    },
    [highestPrice, lowestPrice, query]
  );

  const updatePriceToQueryDebounced = useMemo(
    () => debounce(updatePriceToQuery, 500),
    [updatePriceToQuery]
  );

  useEffect(() => {
    // console.log("max price render: ",{ maxPrice, highestPrice, lowestPrice });
    const v = valueLabelParse(maxPrice, highestPrice, lowestPrice);

    if (isNaN(v)) {
      // setValue([value[0], highestPrice || MAX_PRICE]);
    } else setValue([value[0], v]);
  }, [maxPrice]);

  useEffect(() => {
    // console.log("min price render: ",{ minPrice, highestPrice, lowestPrice });
    const v = valueLabelParse(minPrice, highestPrice, lowestPrice);

    if (isNaN(v)) {
      // setValue([value[0], highestPrice || MAX_PRICE]);
    } else setValue([v, value[1]]);
  }, [minPrice]);

  useEffect(() => {
    pageLoading.current = false;
  }, [products]);

  const sortBy = searchParams?.get(filterQueries.sortBy);
  let sortByQuery = "";
  if (sortBy) {
    sortByQuery = `${filterQueries.sortBy}=${sortBy}&${
      filterQueries.sort
    }=${searchParams?.get(filterQueries.sort)}`;
  }

  const maxPages = Math.ceil(total / perPage);

  const classNames = [classes.Body, className];

  return (
    <div className={classNames.join(" ")} style={style}>
      <div className={`${classes.container} container`}>
        <div
          className={`${classes.left} ${
            productsFilter.openFilterModal && classes.open
          }`}
        >
          {productsFilter.openFilterModal && (
            <div
              className={classes.closeFilter}
              onClick={() => productsFilter.setOpenFilterModal(false)}
            >
              <CloseIcon size="20px" className={classes.icon} />
            </div>
          )}

          <div className={classes.filter}>
            <div className={classes.sort}>
              <p className={classes.title}>Sort by</p>

              <Select
                placeholder="Sort by"
                data={sortByData}
                value={sortByQuery}
                onChange={(value) => {
                  if (!value) return;

                  const sortBy = value?.split("&")?.[0]?.split("=")?.[1];
                  const sort = value?.split("&")?.[1]?.split("=")?.[1];

                  const url = new URLSearchParams();

                  url.set(filterQueries.sortBy, sortBy);
                  url.set(filterQueries.sort, sort);

                  onUrlFilter(url);
                }}
              />
            </div>

            <div className={classes.row}>
              <p className={classes.title}>Price</p>

              {price && (
                <button
                  className={classes.clearButton}
                  onClick={() => {
                    const url = new URLSearchParams();
                    url.set(filterQueries.price, "");

                    onUrlFilter(url);

                    setValue([MIN_PRICE, 50]);
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex flex-col gap-5 mt-7 px-2">
              <RangeSlider
                color="gray"
                opacity={0.8}
                thumbSize={24}
                value={value}
                onChange={(value) => {
                  setValue(value);

                  updatePriceToQueryDebounced(value);
                }}
                minRange={1}
                label={(v) => {
                  return valueLabelFormat(v, highestPrice, lowestPrice);
                }}
                // labelAlwaysOn
              />

              <div className="flex justify-between mt-2 text-sm font-medium">
                <div>
                  <p>
                    Min ৳{" "}
                    {/* {numberWithCommas(
                      valueLabelFormat(value[0], highestPrice, lowestPrice) ??
                        MIN_PRICE
                    )} */}
                  </p>

                  <input
                    type="number"
                    name=""
                    id=""
                    className="w-24 border p-1"
                    value={
                      (valueLabelFormat(value[0], highestPrice, lowestPrice) ??
                        MAX_PRICE) > 0
                        ? valueLabelFormat(
                            value[0],
                            highestPrice,
                            lowestPrice
                          ) ?? MIN_PRICE
                        : ""
                    }
                    onChange={(e) => {
                      const inputValue = +e.target.value;
                      setMinPrice(inputValue);

                      // Convert to slider scale and trigger price update
                      const minSliderValue = valueLabelParse(
                        inputValue,
                        highestPrice,
                        lowestPrice
                      );
                      const maxSliderValue = valueLabelParse(
                        maxPrice,
                        highestPrice,
                        lowestPrice
                      );

                      const clamp = (n: number) =>
                        Math.max(0, Math.min(100, n));
                      let a = clamp(minSliderValue);
                      let b = clamp(maxSliderValue);

                      if (a > b) {
                        const t = a;
                        a = b;
                        b = t;
                      }

                      updatePriceToQueryDebounced([a, b]);
                    }}
                  />
                </div>
                <div>
                  <p>
                    Max ৳{" "}
                    {/* {numberWithCommas(
                      valueLabelFormat(value[1], highestPrice, lowestPrice) ??
                        MAX_PRICE
                    )} */}
                  </p>
                  <input
                    type="number"
                    name=""
                    id=""
                    className="w-24 border p-1"
                    value={
                      (valueLabelFormat(value[1], highestPrice, lowestPrice) ??
                        MAX_PRICE) > 0
                        ? valueLabelFormat(
                            value[1],
                            highestPrice,
                            lowestPrice
                          ) ?? MAX_PRICE
                        : ""
                    }
                    onChange={(e) => {
                      const inputValue = +e.target.value;
                      setMaxPrice(inputValue);

                      // Convert to slider scale and trigger price update
                      const minSliderValue = valueLabelParse(
                        minPrice,
                        highestPrice,
                        lowestPrice
                      );
                      const maxSliderValue = valueLabelParse(
                        inputValue,
                        highestPrice,
                        lowestPrice
                      );

                      const clamp = (n: number) =>
                        Math.max(0, Math.min(100, n));
                      let a = clamp(minSliderValue);
                      let b = clamp(maxSliderValue);

                      if (a > b) {
                        const t = a;
                        a = b;
                        b = t;
                      }

                      updatePriceToQueryDebounced([a, b]);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <Accordion
              multiple={true}
              defaultValue={["Availability"]}
              className="p-0"
            >
              <Accordion.Item
                // key={filterItem.id + "_" + index}
                value={"Availability"}
                className="text-primary-dark mb-1"
              >
                <Accordion.Control
                  className="text-[1.5rem] font-bold mb-0.5"
                  styles={{
                    control: {
                      backgroundColor: "transparent",
                      textTransform: "uppercase",
                    },
                  }}
                >
                  Availability
                </Accordion.Control>

                <Accordion.Panel>
                  {/* <button
                    className={`flex gap-3 mb-1 cursor-pointer`}
                    onClick={() => {
                      if (!!onlineExclusiveQuery) {
                        const url = new URLSearchParams();

                        url.set(filterQueries.onlineExclusive, "");

                        onUrlFilter(url);
                        return;
                      }

                      const url = new URLSearchParams();

                      url.set(filterQueries.onlineExclusive, "true");

                      onUrlFilter(url);
                    }}
                  >
                    <CheckBox checked={!!onlineExclusiveQuery} />

                    <p className={classes.text}>Online Exclusive</p>
                  </button> */}

                  <button
                    className={`flex gap-3 mb-1 cursor-pointer`}
                    onClick={() => {
                      if (!!inStockQuery) {
                        const url = new URLSearchParams();

                        url.set(filterQueries.inStock, "");

                        onUrlFilter(url);
                        return;
                      }

                      const url = new URLSearchParams();

                      url.set(filterQueries.inStock, "true");

                      onUrlFilter(url);
                    }}
                  >
                    <CheckBox checked={!!inStockQuery} />

                    <p className={classes.text}>In Stock</p>
                  </button>

                  <button
                    className={`flex gap-3 mb-1 cursor-pointer`}
                    onClick={() => {
                      if (!!preOrderQuery) {
                        const url = new URLSearchParams();

                        url.set(filterQueries.preOrder, "");

                        onUrlFilter(url);
                        return;
                      }

                      const url = new URLSearchParams();

                      url.set(filterQueries.preOrder, "true");

                      onUrlFilter(url);
                    }}
                  >
                    <CheckBox checked={!!preOrderQuery} />

                    <p className={classes.text}>Pre Order</p>
                  </button>

                  <button
                    className={`flex gap-3 mb-1 cursor-pointer`}
                    onClick={() => {
                      if (!!comingSoonQuery) {
                        const url = new URLSearchParams();

                        url.set(filterQueries.comingSoon, "");

                        onUrlFilter(url);
                        return;
                      }

                      const url = new URLSearchParams();

                      url.set(filterQueries.comingSoon, "true");

                      onUrlFilter(url);
                    }}
                  >
                    <CheckBox checked={!!comingSoonQuery} />

                    <p className={classes.text}>Up Coming</p>
                  </button>

                  <button
                    className={`flex gap-3 mb-1 cursor-pointer`}
                    onClick={() => {
                      if (!!outOfStockQuery) {
                        const url = new URLSearchParams();

                        url.set(filterQueries.outOfStock, "");

                        onUrlFilter(url);
                        return;
                      }

                      const url = new URLSearchParams();

                      url.set(filterQueries.outOfStock, "true");

                      onUrlFilter(url);
                    }}
                  >
                    <CheckBox checked={!!outOfStockQuery} />

                    <p className={classes.text}>Out of Stock</p>
                  </button>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </div>

          {!hideFilter &&
            (type === "online" ? (
              <FilterItemsCategoryTree
                onFilter={onFilter}
                type={type}
                slug={category?.slug}
                subcategories={subcategories}
                filterAttributes={filterAttributes}
              />
            ) : (
              <FilterItemsAccordion
                onFilter={onFilter}
                type={type}
                slug={category?.slug}
                subcategories={subcategories}
                filterAttributes={filterAttributes}
              />
             
            ))}
        </div>

        <div className={classes.right} id={pid}>
          {filterAds.response?.Data && (
            <div className="mb-6">
              {filterAds.response.Data.link ? (
                <Link
                  href={getPageRoute(
                    (filterAds.response.Data.type as PageTypes) || "custom",
                    filterAds.response.Data.link
                  )}
                >
                  <Image
                    src={getS3ImageUrl(
                      filterAds.response.Data.image_url || ""
                    )}
                    alt={filterAds.response.Data.title || "Ad"}
                    width={800}
                    height={200}
                    layout="responsive"
                    unoptimized={true}
                    style={{ objectFit: "contain" }}
                  />
                </Link>
              ) : (
                <Image
                  src={getS3ImageUrl(
                    filterAds.response.Data.image_url || ""
                  )}
                  alt={filterAds.response.Data.title || "Ad"}
                  width={800}
                  height={200}
                  layout="responsive"
                  unoptimized={true}
                  style={{ objectFit: "contain" }}
                />
              )}
            </div>
          )}
          {productsFilter.openFilterModal && (
            <div
              className={classes.overlay}
              onClick={() => productsFilter.setOpenFilterModal(false)}
            ></div>
          )}

          <div className={classes.header}>
            <div>
              <h3 className={classes.name}>{category?.name}</h3>
              <p className={classes.count}>
                {/* {loading
                  ? "Loading..."
                  : `${total} items found ${
                      category?.name ? `in ${category?.name || ""}` : ""
                    }`} */}
                {`${total} items found ${
                  category?.name ? `in ${category?.name || ""}` : ""
                }`}
              </p>
            </div>

            <div className={classes.sort}>
              <Select
                placeholder="Sort by"
                data={sortByData}
                value={sortByQuery}
                onChange={(value) => {
                  if (!value) {
                    const url = new URLSearchParams();

                    url.set(filterQueries.sortBy, "");
                    url.set(filterQueries.sort, "");

                    onUrlFilter(url);
                    return;
                  }

                  const sortBy = value?.split("&")?.[0]?.split("=")?.[1];
                  const sort = value?.split("&")?.[1]?.split("=")?.[1];

                  const url = new URLSearchParams();

                  url.set(filterQueries.sortBy, sortBy);
                  url.set(filterQueries.sort, sort);

                  onUrlFilter(url);
                }}
              />
            </div>

            <button
              className={classes.filterButton}
              onClick={() => productsFilter.setOpenFilterModal(true)}
            >
              <FilterOutlineIcon width={20} height={20} />
            </button>
          </div>

          {/* <Comp
            pageStart={0}
            loadMore={loadMoreHandler}
            threshold={500}
            initialLoad={false}
            hasMore={productsFilter.activePage < maxPages}
            loader={
              <div className="'w-full py-10 flex" key={0}>
                <div className="mx-auto w-10 h-10 rounded-full border border-gray2 border-b-transparent animate-spin"></div>
              </div>
            }
          > */}

          <ProductListWidget
            widgets={widgets}
            products={products}
            loading={loading}
            activePage={productsFilter.activePage}
            maxCol={productsFilter.maxCol}
            perPage={productsFilter.perPage}
            total={total}
          />

          {total > 1 && (
            <PageChange
              className="mt-8"
              activePage={productsFilter.activePage}
              setActivePage={(page) => {
                const url = new URLSearchParams();
                url.set(currentPageQuery, page.toString());

                // setActivePage(page);
                onUrlFilter(url);

                document.getElementById(pid)?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              total={maxPages}
              selectPerPage={true}
              setPerPage={setPerPage}
            />
          )}

          <div
            className="html-content overflow-auto hide-scrollbar pt-12"
            dangerouslySetInnerHTML={{
              __html: category?.shortDescription || "",
            }}
          ></div>

          {category?.showPricingTable && products && products?.length > 0 && (
            <div className="pt-12">
              <h2 className="text-2xl font-medium">
                {category?.pricingTableHeading
                  ? category.pricingTableHeading
                  : `Latest ${category?.name} Price List with Specs ${currentYear}`}
              </h2>

              <div className="space-y-2 ps-2 mt-2">
                <p className="flex justify-between md:gap-8 pb-0.5 font-medium">
                  {category?.name} List <span>Price in BD</span>
                </p>
                {products.slice(0, 9).map((product, index) => (
                  <div
                    key={product.slug || product.name || `product-${index}`}
                    className="pb-1"
                  >
                    <Link
                      href={`/product/${product.slug}`}
                      className="flex justify-between md:gap-8 border-b border-b-gray-400 pb-0.5"
                    >
                      {product?.name}{" "}
                      <span>
                        TK.{" "}
                        {Math.ceil(product?.discounted_price) ||
                          Math.ceil(product?.price)}
                      </span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className="html-content overflow-auto hide-scrollbar pt-12"
            dangerouslySetInnerHTML={{
              __html: category?.description || "",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default Body;
